<template>
  <div v-if="isBrandProtectionEnabled" class="bp">
    <div class="bp__header">
      <div class="bp__title">
        {{ $t('brand.protection.settings.title') }}
      </div>
      <div v-if="isMssp" class="bp__subtitle">
        Per-organization keyword management
      </div>
    </div>

    <!-- ═══ Keywords Section ═══ -->
    <div class="bp__section">
      <div class="bp__section-header">
        <span class="bp__section-title">
          {{ $t('brand.protection.infostealers.keywords') }}
        </span>
        <span
          v-if="!isMssp && keywordCap != null"
          class="bp__cap-label"
          :class="capClass"
        >
          {{ totalKeywordCount }} / {{ keywordCap }}
        </span>
      </div>

      <!-- Usage bar (tenant mode): platform + api keywords share one cap, shown
           as two segments so the split is visible at a glance. -->
      <template v-if="!isMssp && keywordCap">
        <div class="bp__usage-bar" :class="{ 'bp__usage-bar--full': capPct >= 100 }">
          <div
            v-show="platformCount"
            class="bp__usage-seg bp__usage-seg--platform"
            :style="{ width: platformPct + '%' }"
          />
          <div
            v-show="apiCount"
            class="bp__usage-seg bp__usage-seg--api"
            :style="{ width: apiPct + '%' }"
          />
        </div>
        <div class="bp__legend">
          <span class="bp__legend-item">
            <span class="bp__legend-dot bp__legend-dot--platform" />
            Cynergy Platform <b>{{ platformCount }}</b>
          </span>
          <span class="bp__legend-item">
            <span class="bp__legend-dot bp__legend-dot--api" />
            Cynergy API <b>{{ apiCount }}</b>
          </span>
        </div>
      </template>

      <!-- MSSP: org accordion -->
      <mssp-brand-protection
        v-if="isMssp"
        ref="msspKeywords"
      />

      <!-- Tenant: flat keyword list -->
      <template v-else>
        <div class="bp__chips">
          <!-- Platform / legacy keywords: managed here (removable, counted against the cap). -->
          <v-chip
            v-for="(kw, i) in platformKeywords"
            :key="`platform-${i}`"
            label close pill small
            text-color="#2459d5"
            class="bp__chip"
            close-icon="close"
            @click:close="removeKeyword(kw)"
          >
            {{ kw }}
            <span
              v-if="getKeywordProgress(kw)"
              class="bp__chip-progress"
            >{{ getKeywordProgress(kw) }}</span>
          </v-chip>
          <!-- API-hub keywords: shown so API-initiated brand protection reflects
               in the UI, but managed via the API (read-only here). -->
          <v-chip
            v-for="(kw, i) in apiKeywords"
            :key="`api-${i}`"
            label pill small
            text-color="#2459d5"
            class="bp__chip bp__chip--api"
            title="Added via the API — manage it from the API Hub"
          >
            {{ kw }}
            <span class="bp__chip-badge">API</span>
            <span
              v-if="getKeywordProgress(kw)"
              class="bp__chip-progress"
            >{{ getKeywordProgress(kw) }}</span>
          </v-chip>
          <span
            v-if="!keywordsList.length"
            class="bp__empty"
          >No keywords configured</span>
        </div>
        <div class="bp__actions">
          <v-btn
            small
            color="primary"
            class="bp__add-btn"
            :disabled="isKeywordCapped"
            @click="showAddKeywordPopup = true"
          >
            <v-icon small left>add</v-icon>
            {{ $t('add.new.keyword.popup.button') }}
          </v-btn>
          <span v-if="isKeywordCapped" class="bp__cap-msg">
            <v-icon x-small color="error">block</v-icon>
            Keyword limit reached
          </span>
        </div>
      </template>
    </div>

    <!-- ═══ Social Media Profiles Section ═══ -->
    <div class="bp__section">
      <div class="bp__section-header">
        <span class="bp__section-title">
          {{ $t('brand.protection.social.media.profiles') }}
        </span>
      </div>
      <div class="bp__chips">
        <v-chip
          v-for="(profile, i) in socialProfilesList"
          :key="i"
          label close pill small
          text-color="#2459d5"
          class="bp__chip"
          close-icon="close"
          @click:close="removeProfile(profile)"
        >
          <v-icon size="16" class="mr-2">
            {{ getSocialMediaIcon(profile) }}
          </v-icon>
          {{ profile }}
        </v-chip>
        <span
          v-if="!socialProfilesList.length"
          class="bp__empty"
        >No social profiles configured</span>
      </div>
      <div class="bp__actions">
        <v-btn
          small
          class="bp__add-btn"
          @click="showAddProfilePopup = true"
        >
          <v-icon small left>add</v-icon>
          {{ $t('add.new.profile.popup.button') }}
        </v-btn>
      </div>
    </div>

    <add-keyword-popup
      :visiblePopup="showAddKeywordPopup"
      :currentKeywords="platformKeywords"
      @closeAddKeywordPopup="showAddKeywordPopup = false"
      @saveAddKeyword="addKeywords"
    />
    <add-profile-popup
      :visiblePopup="showAddProfilePopup"
      :currentProfiles="socialProfilesList"
      @closeAddProfilePopup="showAddProfilePopup = false"
      @saveAddProfile="addProfiles"
    />
    <error-message
      :errorText="errorMessage"
      @close="errorMessage = ''"
    />
  </div>

  <!-- No license -->
  <div v-else class="bp__no-license">
    <feature-toggle-disabled
      :title="'brand.protection.settings.title'"
      :message="'cloud.services.disabled.popup.message'"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AddKeywordPopup from '@/components/popup/AddKeywordPopup.vue';
import AddProfilePopup from '@/components/popup/AddProfilePopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import FeatureToggleDisabled from '@/components/common/FeatureToggleDisabled.vue';
import MsspBrandProtection from '@/components/Settings/MsspBrandProtection.vue';
import { ADMIN_SYSTEM_SETTINGS, GET_SYSTEM_SETTINGS } from '@/store/user/actionTypes';
import {
  START_INFOSTEALERS_POLLING,
  STOP_INFOSTEALERS_POLLING,
} from '@/store/configuration/actionTypes';
import {
  getSocialMediaPlatform,
  validateSocialMediaLimit,
  validateKeywordsLimit,
} from '@/utils/socialMediaUtils';
import { calculateProgressValue } from '@/utils/infostealersProgress';

export default {
  name: 'BrandProtectionSettings',
  components: {
    FeatureToggleDisabled,
    AddKeywordPopup,
    AddProfilePopup,
    ErrorMessage,
    MsspBrandProtection,
  },
  data() {
    return {
      showAddKeywordPopup: false,
      showAddProfilePopup: false,
      errorMessage: '',
      progressData: {},
    };
  },
  async created() {
    try {
      await this.$store.dispatch(GET_SYSTEM_SETTINGS);
      this.updateProgressData();
      if (this.isBrandProtectionEnabled &&
        !this.isMssp &&
        this.keywordsList.length > 0) {
        this.$store.dispatch(START_INFOSTEALERS_POLLING);
      }
    } catch (e) {
      this.errorMessage = e.message;
    }
  },
  watch: {
    infostealersProgressData: {
      handler() { this.updateProgressData(); },
      deep: true,
    },
  },
  computed: {
    ...mapGetters([
      'systemSettings',
      'hasLicenseModule',
      'isMsspLicense',
      'licenseData',
      'infostealersProgressData',
    ]),
    isMssp() {
      return this.isMsspLicense;
    },
    isBrandProtectionEnabled() {
      if (!this.isMssp) return this.hasLicenseModule('infostealers');
      const orgs = this.licenseData?.organizations || [];
      return orgs.some(o => (o.licenses || []).some(l => !l.isExpired && l.skuDetails?.module === 'infostealers'));
    },
    // Platform-owned keywords: the legacy flat list + the platform bucket. These
    // are the keywords managed from this screen — removable and counted against
    // the licensed keyword cap.
    platformKeywords() {
      const bp = this.systemSettings?.settingsMetaData
        ?.brandProtection;
      const legacy = bp?.keywordsList || [];
      const platformEntries = bp?.keywordsBySource?.platform || [];
      const all = new Set(legacy);
      platformEntries.forEach(e => all.add(typeof e === 'string' ? e : e.keyword));
      return [...all];
    },
    // Keywords added through the API Hub. Shown here so API-initiated brand
    // protection reflects in the UI (read-only — they are managed via the API).
    // The cyn-ai bucket stays hidden: it is owned by the agents hub, not the
    // platform tenant.
    apiKeywords() {
      const bp = this.systemSettings?.settingsMetaData
        ?.brandProtection;
      const apiEntries = bp?.keywordsBySource?.api || [];
      const platform = new Set(this.platformKeywords);
      const seen = new Set();
      const result = [];
      apiEntries.forEach(e => {
        const kw = typeof e === 'string' ? e : e.keyword;
        if (!kw || platform.has(kw) || seen.has(kw)) return;
        seen.add(kw);
        result.push(kw);
      });
      return result;
    },
    // Full list rendered in the UI (platform + api). Drives progress display and
    // the empty-state check; never includes the cyn-ai bucket.
    keywordsList() {
      return [...this.platformKeywords, ...this.apiKeywords];
    },
    socialProfilesList() {
      const bp = this.systemSettings?.settingsMetaData
        ?.brandProtection;
      return bp?.socialProfilesList || [];
    },
    keywordCap() {
      return this.licenseData?.permissions?.keywords ?? null;
    },
    platformCount() {
      return this.platformKeywords.length;
    },
    apiCount() {
      return this.apiKeywords.length;
    },
    // Platform + api keywords share one licensed cap.
    totalKeywordCount() {
      return this.platformCount + this.apiCount;
    },
    isKeywordCapped() {
      return this.keywordCap != null &&
       this.totalKeywordCount >= this.keywordCap;
    },
    capPct() {
      if (!this.keywordCap) return 0;
      return Math.min(
        100,
        Math.round((this.totalKeywordCount / this.keywordCap) * 100),
      );
    },
    // Segment widths for the usage bar. Clamp so platform + api never overflow
    // the track even if usage momentarily exceeds the cap.
    platformPct() {
      if (!this.keywordCap) return 0;
      return Math.min(100, (this.platformCount / this.keywordCap) * 100);
    },
    apiPct() {
      if (!this.keywordCap) return 0;
      return Math.min(100 - this.platformPct, (this.apiCount / this.keywordCap) * 100);
    },
    capClass() {
      if (this.capPct >= 100) return 'bp__cap-label--red';
      if (this.capPct >= 75) return 'bp__cap-label--orange';
      return '';
    },
  },
  methods: {
    getSocialMediaIcon(profile) {
      const p = getSocialMediaPlatform(profile);
      return p ? p.icon : 'mdi-link';
    },
    async removeKeyword(keyword) {
      // Only platform-owned keywords are removable here. The list sent back is
      // persisted as the platform bucket, so basing it on platformKeywords keeps
      // the api / cyn-ai buckets untouched.
      const kws = this.platformKeywords.filter(k => k !== keyword);
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, {
          brandProtection: {
            ...this.systemSettings?.settingsMetaData
              ?.brandProtection,
            keywordsList: kws,
          },
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async addKeywords(keywords) {
      this.showAddKeywordPopup = false;
      const formatted = keywords.map(k => k.keyword.trim().toLowerCase());
      // Adds go into the platform bucket; merge onto the platform list only so
      // api / cyn-ai keywords are never pulled in.
      const current = this.platformKeywords;
      const merged = [...current, ...formatted];
      const maxKw = this.keywordCap || undefined;
      // The cap is shared by platform + api keywords, so validate the combined
      // total even though only the platform bucket is persisted here.
      const { isValid, message } = validateKeywordsLimit(
        [...this.apiKeywords, ...merged],
        maxKw,
      );
      if (!isValid) {
        this.errorMessage = message;
        return;
      }
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, {
          brandProtection: {
            ...this.systemSettings?.settingsMetaData
              ?.brandProtection,
            keywordsList: merged,
          },
        });
        this.updateProgressData();
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async removeProfile(profile) {
      const profiles = this.socialProfilesList
        .filter(p => p !== profile);
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, {
          brandProtection: {
            ...this.systemSettings?.settingsMetaData
              ?.brandProtection,
            socialProfilesList: profiles,
          },
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async addProfiles(profiles) {
      this.showAddProfilePopup = false;
      const formatted = profiles.map(p => p.profile);
      const current = this.socialProfilesList;
      const merged = [...current, ...formatted];
      const { isValid, message } = validateSocialMediaLimit(merged);
      if (!isValid) {
        this.errorMessage = message;
        return;
      }
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, {
          brandProtection: {
            ...this.systemSettings?.settingsMetaData
              ?.brandProtection,
            socialProfilesList: merged,
          },
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    updateProgressData() {
      const pd = this.infostealersProgressData;
      if (pd?.running_jobs?.length > 0) {
        // this.keywordsList spans the platform + api keywords shown in the UI, so
        // progress is displayed for both (never the hidden cyn-ai bucket).
        const jobs = pd.running_jobs.filter(
          j => this.keywordsList.includes(j.keyword),
        );
        jobs.forEach(j => {
          this.$set(this.progressData, j.keyword, j);
        });
      } else {
        this.progressData = {};
      }
    },
    getKeywordProgress(keyword) {
      const d = this.progressData[keyword];
      if (!d) return '';
      const v = calculateProgressValue(d);
      return `(${v}%)`;
    },
  },
  beforeDestroy() {
    this.$store.dispatch(STOP_INFOSTEALERS_POLLING);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.bp {
  height: 100%;
  margin-left: 3%;
  padding: 0 $space-20 $space-10 $space-8;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;

  &__header {
    margin-bottom: $space-3;
    margin-top: 1%;
  }

  &__title {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-xl;
    font-weight: bold;
    line-height: $line-height-tight;
    margin-bottom: 0;
  }

  &__subtitle {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
  }

  &__section {
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-lg;
    padding: $space-3 $space-4;
    background: var(--bg-secondary);
    margin-bottom: $space-3;
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $space-3;
    padding-bottom: $space-1-5;
    border-bottom: $border-width-thin solid var(--border-primary);
  }

  &__section-title {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-base;
  }

  &__cap-label {
    font-family: $font-family-bold;
    font-size: $font-size-sm;
    font-variant-numeric: tabular-nums;
    color: var(--text-secondary);

    &--orange { color: #f97316; }
    &--red { color: $error-500; }
  }

  &__usage-bar {
    display: flex;
    height: $space-2;
    background: $gray-200;
    border-radius: $radius-full;
    overflow: hidden;
    margin-bottom: $space-2;

    // Faint ring when the shared cap is reached, without recoloring the
    // source segments (the split stays readable).
    &--full {
      box-shadow: inset 0 0 0 1px rgba($error-500, 0.55);
    }
  }

  &__usage-seg {
    height: 100%;
    min-width: 3px;
    // Snappy, crisp fill — strong ease-out so the bar feels responsive.
    transition: width 220ms cubic-bezier(0.23, 1, 0.32, 1);

    &--platform {
      background: linear-gradient(90deg, #6366f1, #818cf8);
    }

    &--api {
      background: linear-gradient(90deg, #94a3b8, #cbd5e1);
    }

    // Hairline between adjacent segments so they read as two parts.
    & + & {
      box-shadow: inset 1px 0 0 var(--bg-secondary);
    }
  }

  &__legend {
    display: flex;
    gap: $space-4;
    margin-bottom: $space-4;
    font-size: $font-size-xs;
    color: var(--text-secondary);
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: $space-1-5;

    b {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-variant-numeric: tabular-nums;
    }
  }

  &__legend-dot {
    width: 8px;
    height: 8px;
    border-radius: $radius-full;

    &--platform { background: #6366f1; }
    &--api { background: #94a3b8; }
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: $space-2-5;
    margin-bottom: $space-1;
  }

  &__chip {
    background-color: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.18);
    margin: 0;
    font-size: $font-size-sm;
    &.v-chip--label { border-radius: 6px; }
  }

  &__chip--api {
    background-color: rgba(148, 163, 184, 0.10);
    border: 1px solid rgba(148, 163, 184, 0.28);
  }

  &__chip-badge {
    margin-left: $space-1;
    padding: 0 $space-1;
    font-size: $font-size-xs;
    font-family: $font-family-bold;
    color: var(--text-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__chip-progress {
    margin-left: $space-1;
    font-size: $font-size-xs;
    color: var(--text-secondary);
  }

  &__empty {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    font-style: italic;
    padding: $space-3 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $space-3;
    margin-top: $space-3;
  }

  &__add-btn {
    text-transform: none;
    letter-spacing: normal;
  }

  &__cap-msg {
    display: flex;
    align-items: center;
    gap: $space-1;
    font-size: $font-size-sm;
    color: $error-500;
  }

  &__no-license {
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 190px);
  }
}
</style>
